import React, { useState } from "react";
import { Plus, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import UsersTable from "./components/UsersTable";
import AddUserDialog from "./components/AddUserDialog";
import { useAuth } from "@/contexts/AuthContext";
import { FilterType, UserData } from "./types/userTypes";
import { useUserData } from "./hooks/useUserData";
import UserStatsCards from "./components/UserStatsCards";
import UserListActions from "./components/UserListActions";
import { exportUserData } from "./utils/exportUtils";

const UsersListPage: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [isAddUserDialogOpen, setIsAddUserDialogOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState<FilterType | null>(null);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>("all");
  const { user } = useAuth();
  
  // Fetch organizations for the filter dropdown
  const { data: organizations } = useQuery({
    queryKey: ["organizations"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organizations")
        .select("*")
        .order("name");
      
      if (error) throw error;
      return data;
    }
  });
  
  const {
    data,
    isLoading,
    error,
    refetch,
  } = useUserData(currentPage, pageSize, searchTerm, activeFilter || 'all', selectedOrganizationId);
  
  React.useEffect(() => {
    if (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to load users. Please try refreshing.");
    }
  }, [error]);
  
  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };
  
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleFilter = (filter: FilterType | null) => {
    setActiveFilter(filter);
    setCurrentPage(1);
  };

  const handleExport = (format: 'csv' | 'excel' | 'pdf') => {
    if (data?.users) {
      exportUserData(data.users as UserData[], format);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <h1 className="text-3xl font-bold tracking-tight">Users</h1>
        <Button onClick={() => setIsAddUserDialogOpen(true)}>
          <Plus className="mr-2 h-4 w-4" />
          Add New User
        </Button>
      </div>
      
      <UserStatsCards 
        userCounts={data?.userCounts || { admin: 0, user: 0 }}
        isLoading={isLoading}
      />
      
      {/* Organization Filter */}
      <div className="flex items-center gap-2">
        <Filter className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm text-muted-foreground">Filter by Organization:</span>
        <Select 
          value={selectedOrganizationId} 
          onValueChange={(value) => {
            setSelectedOrganizationId(value);
            setCurrentPage(1); // Reset to first page when filter changes
          }}
        >
          <SelectTrigger className="w-64">
            <SelectValue placeholder="Select organization" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Organizations</SelectItem>
            {organizations?.map((organization) => (
              <SelectItem key={organization.id} value={organization.id}>
                {organization.name}
              </SelectItem>
            )) || []}
          </SelectContent>
        </Select>
      </div>
      
      <UserListActions
        searchTerm={searchTerm}
        onSearchChange={handleSearch}
        activeFilter={activeFilter}
        onFilterChange={handleFilter}
        onExport={handleExport}
      />
      
      {activeFilter && data?.users.length === 0 && !isLoading && (
        <div className="rounded-md border p-8 text-center">
          <div className="text-lg font-medium">No Users Found</div>
          <div className="text-muted-foreground mt-2">Try adjusting your filters</div>
          <Button onClick={() => handleFilter(null)} variant="outline" className="mt-4">
            Clear Filters
          </Button>
        </div>
      )}
      
      <UsersTable
        users={data?.users || []}
        isLoading={isLoading}
        currentPage={currentPage}
        pageSize={pageSize}
        totalItems={data?.total || 0}
        onPageChange={handlePageChange}
        onRefresh={refetch}
      />
      
      <AddUserDialog
        isOpen={isAddUserDialogOpen}
        onClose={() => setIsAddUserDialogOpen(false)}
        onSuccess={() => refetch()}
      />
    </div>
  );
};

export default UsersListPage;
